import { getNodeDataByTimestamps } from 'services/devices/devices.service';

/** Points for each device in one export page. The backend's maximum. */
export const EXPORT_PAGE_LIMIT = 5000;

/** Device ids in one request. The backend answers 400 above this. */
export const EXPORT_IDS_PER_REQUEST = 25;

/**
 * The deepest `offset` the backend accepts (its `max-offset` config key, read by
 * the range schema). Asking past it answers 400, which would throw away every
 * page already collected — so the walk stops at the last legal page instead.
 */
export const EXPORT_MAX_OFFSET = 100000;

export const chunkExportIds = (ids, size = EXPORT_IDS_PER_REQUEST) => {
  const list = Array.isArray(ids) ? ids : [];
  const chunks = [];
  for (let index = 0; index < list.length; index += size) {
    chunks.push(list.slice(index, index + size));
  }
  return chunks;
};

const seriesUid = (points) => points.find((point) => point?.uid != null)?.uid;

/**
 * Every point for `ids` inside the window, in the tier-2 `result` shape: one
 * inner array for each device, newest first.
 *
 * The endpoint answers one page for at most 25 devices at a time, so this
 * walks the ids in chunks of 25 and each chunk in pages of `limit` until
 * `page.hasMore` is false. Pages are appended in order, which keeps each
 * device's series newest first. A device that returned no points is left out.
 *
 * Throws when a page request gives no response (the service logs the error and
 * resolves `undefined`), so the caller can report a failed export instead of
 * silently exporting a partial file.
 *
 * A device with more points than `EXPORT_MAX_OFFSET` cannot be walked to the
 * end: the request that would reach them is one the backend refuses. Rather
 * than lose the whole export to that 400, the walk stops at the last legal page
 * and calls `onCapReached` once so the caller can say the file is cut short.
 *
 * @param {{
 *   startTime: number,
 *   endTime: number,
 *   ids: Array<number | string>,
 *   limit?: number,
 *   onProgress?: (progress: { rows: number, pages: number }) => void,
 *   onCapReached?: (cap: { ids: Array<number | string>, offset: number }) => void,
 * }} options
 *   `onCapReached` receives the ids of the chunk that stopped and the `offset`
 *   that was NOT requested — the first page beyond the backend's ceiling.
 * @returns {Promise<Array<Array<object>>>}
 */
export const fetchAllNodeDataPages = async ({
  startTime,
  endTime,
  ids,
  limit = EXPORT_PAGE_LIMIT,
  onProgress,
  onCapReached,
}) => {
  const seriesByUid = new Map();
  let rows = 0;
  let pages = 0;
  let capReported = false;

  for (const chunk of chunkExportIds(ids)) {
    let offset = 0;
    let hasMore = true;
    while (hasMore) {
      const response = await getNodeDataByTimestamps(startTime, endTime, chunk, { limit, offset });
      if (!response?.data) {
        throw new Error(
          `Device data request failed for ids [${chunk.join(', ')}] at offset ${offset}`,
        );
      }

      const result = Array.isArray(response.data.result) ? response.data.result : [];
      result.forEach((points) => {
        if (!Array.isArray(points) || points.length === 0) return;
        const uid = seriesUid(points);
        if (uid == null) return;
        if (!seriesByUid.has(uid)) seriesByUid.set(uid, []);
        seriesByUid.get(uid).push(...points);
        rows += points.length;
      });

      pages += 1;
      onProgress?.({ rows, pages });
      hasMore = Boolean(response.data.page?.hasMore);
      offset += limit;

      // The next page is past what the backend will serve. Stop this chunk with
      // what it has: the request would 400 and cost the caller the whole export.
      if (hasMore && offset > EXPORT_MAX_OFFSET) {
        hasMore = false;
        if (!capReported) {
          capReported = true;
          onCapReached?.({ ids: chunk, offset });
        }
      }
    }
  }

  return [...seriesByUid.values()];
};
